/**
 * Thin, typed wrapper around the Meta Pixel (`window.fbq`), plus its
 * consent-gated loader.
 *
 * The Meta Pixel sets advertising cookies and sends hits to facebook.com/tr,
 * which Quebec Law 25 (Bill 64) and PIPEDA treat as non-essential tracking that
 * requires opt-in BEFORE it runs. So, unlike before, the pixel base code is NOT
 * loaded eagerly from index.html. Nothing about Meta touches the visitor until
 * `initMetaPixel()` runs, and that only happens after the visitor accepts in
 * CookieConsent (see src/main.tsx). Default state = pixel dark.
 *
 * Every tracking helper double-checks consent and no-ops safely when fbq is
 * absent (no consent yet, SSR, ad-blockers) so callers never need to guard.
 */

import { hasAnalyticsConsent } from "@/lib/consent";

// Meta dataset "C&R Pixel's". Was hard-coded in the index.html snippet; it now
// lives here as the single place the pixel is configured.
const PIXEL_ID = "1401628942000948";

// localStorage key holding the last-seen ad-click id (`fbclid`) so it survives
// a deferred/consent-gated pixel init and a same-origin SPA navigation that
// strips the query string before the pixel ever loads.
const FBCLID_STORAGE_KEY = "cr:fbclid:v1";

// Meta's `_fbc` cookie lives 90 days by default; mirror that as the ceiling for
// how long a captured fbclid stays usable for attribution.
const FBC_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
const FBC_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;

type FbqParams = Record<string, unknown>;

interface FbqEventOptions {
  /**
   * Meta dedup key. When the same event_name + eventID arrives from both the
   * browser pixel and server-side CAPI within ~48h, Meta counts it once. See
   * docs/meta-capi-scope.md — CompleteRegistration uses the Supabase user id.
   */
  eventID?: string;
}

interface Fbq {
  (command: "track" | "trackCustom", event: string, params?: FbqParams, opts?: FbqEventOptions): void;
  (command: "init", pixelId: string): void;
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[];
  loaded?: boolean;
  version?: string;
  push?: unknown;
}

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

function fbq(): Fbq | undefined {
  return typeof window !== "undefined" ? window.fbq : undefined;
}

/**
 * Ad-click attribution (`_fbc`) — the Meta counterpart to the Google Ads
 * cross-domain linker in google-ads.ts.
 *
 * Meta attributes a conversion to the ad that drove it via the `_fbc` cookie,
 * which fbevents.js only ever creates when the `fbclid` URL param is present at
 * the exact moment the pixel initializes. In our funnel it usually is NOT:
 *  - the pixel is deferred (requestIdleCallback) and consent-gated, so it loads
 *    well after the landing paint — often after a client-side route change that
 *    has already dropped `?fbclid=` from the URL; and
 *  - paid traffic hops ad -> go.cipherandrow.com -> .../signup, and the pixel
 *    that fires the CompleteRegistration runs on the later hop.
 * The live pixel data shows the symptom precisely: ViewContent (fires on the ad
 * landing, fbclid still in the URL) has ~100% fbc coverage, while
 * CompleteRegistration (fires post-navigation) has ~0% — so sign-ups land in
 * Events Manager but never attribute to the campaign that paid for them.
 *
 * Fix: grab `fbclid` as early as possible (captureFbclid, called synchronously
 * on first paint from main.tsx, ahead of the deferred pixel) and, once consent
 * is granted, write `_fbc` ourselves in Meta's canonical
 * `fb.1.<creation-ms>.<fbclid>` format, scoped to the registrable domain so it
 * carries across every cipherandrow.com subdomain hop. fbevents then attaches
 * fbc to every event it sends, CompleteRegistration included.
 */

/** True when a `_fbc` cookie already exists (set by us or by fbevents.js). */
function hasFbcCookie(): boolean {
  return typeof document !== "undefined" && /(?:^|;\s*)_fbc=/.test(document.cookie);
}

/**
 * Accept a `fbclid` only if it's a well-formed click id (Meta's are base64url-
 * ish). Guards against a stray `;`/whitespace from the query string breaking the
 * cookie string or injecting extra cookie attributes.
 */
function sanitizeFbclid(raw: string | null | undefined): string | null {
  if (!raw) return null;
  return /^[A-Za-z0-9_.-]{1,512}$/.test(raw) ? raw : null;
}

/**
 * Registrable-domain cookie scope (".cipherandrow.com") so `_fbc` is shared by
 * every subdomain in the ad -> landing -> signup journey. Returns null for
 * localhost / bare IPs so the cookie falls back to host-only (dev, previews).
 * Pragmatic last-two-labels heuristic — correct for our .com; a multi-part TLD
 * (.co.uk) would need a public-suffix list, which we don't ship (see the same
 * trade-off noted in google-ads.ts).
 */
function registrableCookieDomain(hostname: string): string | null {
  if (!hostname || hostname === "localhost" || hostname.endsWith(".localhost")) return null;
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(hostname) || hostname.includes(":")) return null; // IPv4 / IPv6
  const labels = hostname.split(".");
  if (labels.length < 2) return null;
  return "." + labels.slice(-2).join(".");
}

/** Write the `_fbc` cookie in Meta's canonical format. Caller ensures consent. */
function writeFbcCookie(fbclid: string, creationMs: number): void {
  const value = `fb.1.${creationMs}.${fbclid}`;
  const parts = [`_fbc=${value}`, "path=/", `max-age=${FBC_MAX_AGE_SECONDS}`, "SameSite=Lax"];
  const domain = registrableCookieDomain(window.location.hostname);
  if (domain) parts.push(`domain=${domain}`);
  if (window.location.protocol === "https:") parts.push("Secure");
  document.cookie = parts.join("; ");
}

/**
 * Persist the ad-click id from the current URL so it survives the deferred,
 * consent-gated pixel init and same-origin SPA navigations. Call this as early
 * as possible on every entry (main.tsx), BEFORE the pixel loads.
 *
 * This only stores a first-party value in localStorage — it does not contact
 * Meta or set the `_fbc` advertising cookie, so it is safe to run before the
 * consent decision (Quebec Law 25 / PIPEDA). The cookie and every Meta hit stay
 * gated on consent below. Buffering the click id pre-consent mirrors how Google
 * Consent Mode preserves gclid for a later opt-in.
 */
export function captureFbclid(): void {
  if (typeof window === "undefined") return;
  try {
    const fbclid = sanitizeFbclid(new URLSearchParams(window.location.search).get("fbclid"));
    if (!fbclid) return;
    window.localStorage.setItem(FBCLID_STORAGE_KEY, JSON.stringify({ fbclid, ts: Date.now() }));
  } catch {
    // localStorage unavailable (private mode / SSR) — nothing to persist.
  }
}

/**
 * Ensure a `_fbc` cookie exists before the pixel fires, so events attribute to
 * the ad click. No-ops without consent (it sets an advertising cookie) and
 * respects any `_fbc` already present. Prefers a fresh fbclid still in the URL,
 * else the one captureFbclid() stashed, ignoring stashed ids older than 90 days.
 */
export function ensureFbcCookie(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (!hasAnalyticsConsent()) return;
  if (hasFbcCookie()) return;

  let fbclid: string | null = null;
  let creationMs = Date.now();
  try {
    const fromUrl = sanitizeFbclid(new URLSearchParams(window.location.search).get("fbclid"));
    if (fromUrl) {
      fbclid = fromUrl;
    } else {
      const raw = window.localStorage.getItem(FBCLID_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { fbclid?: string; ts?: number };
        const stored = sanitizeFbclid(parsed?.fbclid);
        if (
          stored &&
          typeof parsed.ts === "number" &&
          Date.now() - parsed.ts < FBC_MAX_AGE_MS
        ) {
          fbclid = stored;
          creationMs = parsed.ts;
        }
      }
    }
  } catch {
    // Bad JSON / storage blocked — treat as no captured click id.
  }
  if (!fbclid) return;
  writeFbcCookie(fbclid, creationMs);
}

// Tracks whether we have actually injected the pixel. Guards against a double
// load if initMetaPixel() is called more than once (idle callback + consent
// event in the same session).
let initialized = false;

/**
 * Load the Meta Pixel and fire the initial PageView.
 *
 * No-ops unless the visitor has accepted analytics consent, so this is safe to
 * call eagerly — the consent check is the gate. Mirrors initPostHog().
 */
export function initMetaPixel(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (!hasAnalyticsConsent()) return;
  if (initialized || window.fbq) return;
  initialized = true;

  // Standard Meta Pixel base snippet, ported from the old inline index.html
  // script. Injects connect.facebook.net/en_US/fbevents.js, then init + the
  // first PageView. Because this runs post-consent, no Meta hit precedes opt-in.
  /* eslint-disable @typescript-eslint/no-explicit-any */
  (function (f: any, b: Document, e: string, v: string) {
    if (f.fbq) return;
    const n: any = (f.fbq = function (this: unknown, ...args: unknown[]) {
      n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
    });
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];
    const t = b.createElement(e) as HTMLScriptElement;
    t.async = true;
    t.src = v;
    const s = b.getElementsByTagName(e)[0];
    // Insert before the first existing <script>, or fall back to appending to
    // <head>/<body> when none exists (e.g. a doc that loads us inline-free).
    if (s && s.parentNode) {
      s.parentNode.insertBefore(t, s);
    } else {
      (b.head ?? b.body ?? b.documentElement).appendChild(t);
    }
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable @typescript-eslint/no-explicit-any */

  // Materialize `_fbc` from a captured ad-click id BEFORE init/PageView, so
  // fbevents attaches ad-click attribution to this and every later event
  // (CompleteRegistration included). No-op if `_fbc` already exists.
  ensureFbcCookie();

  fbq()?.("init", PIXEL_ID);
  fbq()?.("track", "PageView");
}

/**
 * Fire a Meta *standard* event (PageView, CompleteRegistration,
 * InitiateCheckout, Purchase, Lead, …). Use trackPixelCustom for non-standard
 * event names so Meta classifies them correctly.
 *
 * No-ops without consent — these never re-arm the pixel, so a visitor who
 * declined (or has not decided) never produces a Meta hit.
 */
export function trackPixel(event: string, params?: FbqParams, opts?: FbqEventOptions) {
  if (!hasAnalyticsConsent()) return;
  // Last-chance ad-click attribution: if a fbclid was captured after the pixel
  // had already initialized, write `_fbc` now so this conversion carries it.
  // fbevents reads document.cookie synchronously at fire time. No-op otherwise.
  ensureFbcCookie();
  fbq()?.("track", event, params, opts);
}

/** Fire a custom (non-standard) Meta event. No-ops without consent. */
export function trackPixelCustom(event: string, params?: FbqParams) {
  if (!hasAnalyticsConsent()) return;
  fbq()?.("trackCustom", event, params);
}

/**
 * Re-fire PageView · used on client-side route changes in this SPA.
 * No-ops without consent.
 */
export function trackPixelPageView() {
  if (!hasAnalyticsConsent()) return;
  fbq()?.("track", "PageView");
}
